import React from 'react';
import { MapPin, Users, Sparkles } from 'lucide-react';
interface RewardCardProps {
  reward: {
    id: string;
    name: string;
    type: string;
    cost: number;
    description: string;
    availability: {
      current: number;
      total: number;
      unlimited: boolean;
    };
    redemptions: {
      total: number;
    };
    location: string;
  };
  isPreview?: boolean;
}
export const RewardCard: React.FC<RewardCardProps> = ({
  reward,
  isPreview = false
}) => {
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'Prodotto Viso':
        return 'bg-purple-100 text-purple-800';
      case 'Prodotto Capelli':
        return 'bg-blue-100 text-blue-800';
      case 'Prodotto Corpo':
        return 'bg-pink-100 text-pink-800';
      case 'Cosmetici Solidi':
        return 'bg-green-100 text-green-800';
      case 'Esperienza':
        return 'bg-orange-100 text-orange-800';
      case 'Buono Sconto':
        return 'bg-indigo-100 text-indigo-800';
      case 'Kit Prodotti':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get representative image/emoji based on product type and name
  const getProductImage = () => {
    const name = reward.name.toLowerCase();
    const type = reward.type;
    // Specific product images
    if (name.includes('siero') || name.includes('acido ialuronico')) {
      return '💧'; // Serum/droplet
    }
    if (name.includes('cosmetici solidi') || name.includes('shampoo solido')) {
      return '🧼'; // Solid soap
    }
    if (name.includes('crema viso') || name.includes('anti-age')) {
      return '🌸'; // Face cream
    }
    if (name.includes('hennetica') || name.includes('tinte')) {
      return '🎨'; // Hair dye
    }
    if (name.includes('workshop') || name.includes('esperienza')) {
      return '👩‍🔬'; // Workshop/experience
    }
    if (name.includes('buono') || name.includes('sconto')) {
      return '🎟️'; // Voucher/ticket
    }
    if (name.includes('crema corpo')) {
      return '🧴'; // Body lotion
    }
    if (name.includes('box') || name.includes('kit')) {
      return '📦'; // Box/kit
    }
    // Fallback by type
    switch (type) {
      case 'Prodotto Viso':
        return '🌸';
      case 'Prodotto Capelli':
        return '💆‍♀️';
      case 'Prodotto Corpo':
        return '🧴';
      case 'Cosmetici Solidi':
        return '🧼';
      case 'Esperienza':
        return '✨';
      case 'Buono Sconto':
        return '🎟️';
      case 'Kit Prodotti':
        return '📦';
      default:
        return '🌿';
    }
  };
  const isPopular = reward.redemptions.total > 150;
  const isLowStock = !reward.availability.unlimited && reward.availability.current < reward.availability.total * 0.3;
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group" data-id="element-568">
      {/* Image Placeholder */}
      <div className="relative h-48 bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center" data-id="element-569">
        <div className="text-7xl" data-id="element-570">{getProductImage()}</div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-571">
          {isPopular && <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium" data-id="element-572">
              <Sparkles className="h-3 w-3" data-id="element-573" />
              Popolare
            </span>}
          {isLowStock && <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium" data-id="element-574">
              Ultimi disponibili
            </span>}
        </div>

        {/* Cost Badge */}
        <div className="absolute top-3 right-3 bg-greenback text-white px-3 py-1.5 rounded-full font-bold text-sm shadow-lg" data-id="element-575">
          {reward.cost} punti
        </div>
      </div>

      {/* Content */}
      <div className="p-5" data-id="element-576">
        {/* Category Badge */}
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-3 ${getTypeBadgeColor(reward.type)}`} data-id="element-577">
          {reward.type}
        </span>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-greenback transition-colors" data-id="element-578">
          {reward.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2" data-id="element-579">
          {reward.description}
        </p>

        {/* Footer Info */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-4" data-id="element-580">
          <div className="flex items-center gap-1" data-id="element-581">
            <MapPin className="h-3.5 w-3.5" data-id="element-582" />
            <span data-id="element-583">{reward.location}</span>
          </div>
          {!reward.availability.unlimited && <div className="flex items-center gap-1" data-id="element-584">
              <Users className="h-3.5 w-3.5" data-id="element-585" />
              <span data-id="element-586">{reward.availability.current} disponibili</span>
            </div>}
        </div>

        {/* CTA Button */}
        <button disabled={isPreview} className={`w-full py-2.5 rounded-lg font-medium text-sm transition-all ${isPreview ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-greenback text-white hover:bg-greenback/90 hover:shadow-md'}`} title={isPreview ? 'Anteprima - Riscatto non disponibile' : 'Riscatta premio'} data-id="element-587">
          {isPreview ? 'Anteprima' : 'Riscatta'}
        </button>
      </div>
    </div>;
};